/**
 * SeatCard.tsx —— 席位卡（设计方案 4.4）
 *
 * 状态穷举：待行动（高亮 + 计时）/ 思考中 / 已弃牌（整卡降透明）/ 全下 / 赢家 / 已淘汰 / 观战。
 * HUD 指标样本不足 20 手时显示短横线，不显示误导性的极端值。
 */

import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Player } from '../core/game';
import type { SeatDensity } from './seatLayout';
import type { StyleKey } from '../core/ai';
import type { SeatStatsView } from '../state/stats';
import { chips, pillInfo, splitNickname, styleName } from '../state/format';
import { STYLE_DESCRIPTIONS } from '../state/opponents';
import { STYLE_AVATARS } from './avatars';
import { PlayingCard, Tip } from './primitives';
import { placeStyleCard, type StyleCardPlacement } from './overlayPlacement';
import { collectProtectedZones, solveStyleCardBottomSafe, OVERLAY_GAP } from './overlayReservation';
import type { Bubble } from '../state/controller';

/** 非结算态的空高亮集合（稳定引用，避免每次渲染新建）。 */
const EMPTY_WIN_SET: ReadonlySet<number> = new Set<number>();

export function SeatCard({
  player,
  isButton,
  isSmallBlind = false,
  isBigBlind = false,
  isToAct,
  thinking,
  isWinner,
  bubble,
  stats,
  style,
  reveal,
  showdownValue,
  winningCards = EMPTY_WIN_SET,
  clockRatio,
  density,
  betDecoupled = false,
  satellitesDecoupled = false,
  innerSide,
  categoryLabel = null,
}: {
  player: Player;
  isButton: boolean;
  /** 本手小盲位（dead SB 不传 true，由上层判定）。 */
  isSmallBlind?: boolean;
  /** 本手大盲位。 */
  isBigBlind?: boolean;
  isToAct: boolean;
  thinking: boolean;
  isWinner: boolean;
  bubble: Bubble | null;
  stats: SeatStatsView;
  style: StyleKey | null;
  /** 是否明牌展示底牌（我方，或摊牌 / 复盘揭示）。 */
  reveal: boolean;
  showdownValue: string | null;
  /** R3/R5：结算时需高亮的制胜 5 张牌集合；底牌命中即上高亮描边。默认空集（非结算态）。 */
  winningCards?: ReadonlySet<number>;
  /** 行动计时剩余比例（0–1），null 表示不显示。 */
  clockRatio: number | null;
  /**
   * 信息密度档位（由 `seatLayout` 按「容器 × 人数」算出，不再由断点决定）：
   *   - `full`：底牌 + 名字 + 筹码 + 状态 + HUD，本街投入内联在筹码行（保证卡片盒恒为标称高度）；
   *   - `compact`：去掉 HUD，本街投入独占一行；
   *   - `row`：横向紧凑（迷你牌面 + 两行文字），状态与风格标签由卡片视觉状态与 aria-label 承载；
   *   - `micro`：单行（名字 + 筹码 + 本街投入），只在极窄视口 + 满桌时出现。
   */
  density: SeatDensity;
  /**
   * 竖屏环布（stack-ring）下对手的「本街投入」由 Table 的 `.felt__chips` 独立层承载（AC3-3 chip 解耦），
   * 此时对手 pod 内不再渲染注额（避免与外部 chip 重复 / 压盖）。真人不受影响（仍走卡内幽灵占位行）。
   */
  betDecoupled?: boolean;
  /**
   * 阶段1 统一占位（方案 B）：竖屏环布对手的手牌牌背与 SB/BB 徽标改由 Table 的 felt 级
   * `.felt__cards` / `.felt__blinds` 层按 `solveOverlayLayout` 下发坐标渲染（统一占位表 / OBB-SAT，
   * AC-L2/L3/L5/L6）。为真时本组件**不渲染**对手 pod 内的 `.seat__cards`（牌背）与 `.seat__badges`
   * （SB/BB/D），避免与 felt 层双份、也彻底移除旧的 CSS 边缘锚定路径。英雄 / oval / grid 不受影响。
   */
  satellitesDecoupled?: boolean;
  /**
   * FR-2：竖屏环布下对手手牌扇形的**朝桌内侧**（由 Table 按 pod 屏幕分区下发）：
   * 左列→'right'（贴 pod 右缘）、右列→'left'（贴左缘）、顶弧→'top'（贴上缘）。
   * 落到 `.seat__cards[data-inner-side]`，把手牌从 pod flex 流移出为 absolute 浮层，
   * 与头像/昵称/筹码/徽标两两不重叠（AC2-1/2-2/2-5）。undefined = 不解耦（英雄/oval/grid）。
   */
  innerSide?: 'left' | 'right' | 'bottom';
  /**
   * FR-5：英雄牌型提示文案（仅英雄传入，来自 controller.heroCategory）；null 不渲染。
   */
  categoryLabel?: string | null;
}) {
  /**
   * R6：对手风格默认收敛。头像 + 昵称区是一枚 `button`（aria-expanded），点击展开「风格卡」，
   * 再次点击 / 点外部 / Esc / ✕ 收起；一次只开一个（打开新的自动收起旧的由各卡独立的
   * document 监听 + 「点外部收起」共同保证：点到另一张卡属于「点外部」→ 先收起本卡）。
   */
  const isOpp = !player.isHuman;
  const canExpand = isOpp && style !== null;
  /**
   * AC-D2：昵称墨迹居中 + 唯一后缀不可截断。把「本名 + 序号」拆开渲染：
   * 本名走 ellipsis（`.seat__name-base`），序号 `#N`（`.seat__name-suffix`）`flex:none` 永不省略，
   * 保证满桌窄档下同名玩家（闪电风暴 #2/#3）仍可辨识。无序号时只渲染本名。
   */
  const nick = splitNickname(player.name);
  const nameChildren = nick.suffix ? (
    <>
      <span className="seat__name-base">{nick.base}</span>
      <span className="seat__name-suffix">{nick.suffix}</span>
    </>
  ) : (
    <span className="seat__name-base">{nick.base}</span>
  );
  /** 头像：全档显示（含 micro，竖屏环布 28px；极窄档由 CSS 收缩，非竖屏 micro 由 CSS 隐藏让位）。 */
  const showAvatar = style !== null;
  const [expanded, setExpanded] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  /** R2-问题2：触发器（头像/昵称按钮），风格卡定位以它的屏幕矩形为锚。 */
  const triggerRef = useRef<HTMLButtonElement>(null);
  /** 风格卡本体（portal 到 overlay 根后仍需测量其自然高以求解限高 + clamp）。 */
  const cardRef = useRef<HTMLDivElement>(null);
  /** 风格卡 portal 目标 = overlay 根 `.gs__overlay`（平面 C，isolate，越过毛毡裁剪 / board40）。 */
  const [overlayRoot, setOverlayRoot] = useState<HTMLElement | null>(null);
  /** 风格卡放置结果（视口坐标 + 限高），null 表示尚未求解（首帧隐藏，避免闪跳）。 */
  const [cardPos, setCardPos] = useState<StyleCardPlacement | null>(null);
  const cardId = useId();

  useEffect(() => {
    if (!expanded) {
      setCardPos(null);
      return;
    }
    setOverlayRoot(document.querySelector<HTMLElement>('.gs__overlay'));
    const onDocPointer = (e: PointerEvent) => {
      const t = e.target as Node;
      /* portal 后风格卡不在 rootRef 子树内，需一并排除 cardRef，否则点卡内即被判为「点外部」立即收起。 */
      if (rootRef.current?.contains(t) || cardRef.current?.contains(t)) return;
      setExpanded(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpanded(false);
    };
    document.addEventListener('pointerdown', onDocPointer, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDocPointer, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [expanded]);

  /**
   * R2-问题2 风格卡放置求解（设计 §4.2.2）：按触发器屏幕位置翻转（左列向右 / 右列向左 /
   * 顶弧向下 / 底部向上）+ 四边 clamp + width:min(240,100vw−16)。open / 滚动 / resize 都重解，
   * 保证「完整落视口内、不溢出屏幕左缘、不被英雄区遮挡」。
   */
  useLayoutEffect(() => {
    if (!expanded) return;
    const compute = () => {
      const trg = triggerRef.current;
      const card = cardRef.current;
      if (!trg || !card) return;
      const tr = trg.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const cardW = Math.min(240, vw - 16);
      /* 先按当前宽量自然高（card 已渲染，宽被 CSS 锁死为 min(240,100vw−16)）。 */
      const cardH = card.scrollHeight;
      /*
       * Round26 B类③ + BUG-R26-01：把「底部操作受保护带 PZ1」与「Hero 席位/手牌区 PZ3」
       * 一并纳入详情卡的翻转/夹取决策。bottomSafe 从旧默认「视口高 − margin」收紧到
       * min(PZ1 顶沿, PZ3 顶沿) − gap（取两者更高者），于是左下 / 靠底头像触发时
       * placeStyleCard 会强制向上/右上翻转并二次 clamp，详情卡底边不再越过操作带顶沿
       * （不遮 ActionBar / sizer CTA），也不压 Hero 席位与两张底牌（∩PZ1=0 且 ∩PZ3=0），
       * 更不压顶栏。采集失败（如隔离单测）时回退旧默认，行为不变。
       */
      const zones = collectProtectedZones();
      const heroTop = zones.pz3Hero ? zones.pz3Hero.top : zones.heroCardsTop;
      const bottomSafe = solveStyleCardBottomSafe({
        bottomSafeBandTop: zones.bottomSafeBandTop,
        heroTop,
        viewportH: vh,
      });
      const topSafe = zones.topbarBottom != null ? zones.topbarBottom + OVERLAY_GAP : undefined;
      setCardPos(
        placeStyleCard({
          trigger: { left: tr.left, top: tr.top, right: tr.right, bottom: tr.bottom, width: tr.width, height: tr.height },
          card: { w: cardW, h: cardH },
          viewport: { w: vw, h: vh },
          bottomSafe,
          topSafe,
        }),
      );
    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [expanded]);

  const avatarNode =
    showAvatar && style !== null ? (
      <span className="seat__avatar" aria-hidden="true">
        <img className="seat__avatar-img" src={STYLE_AVATARS[style]} alt="" loading="lazy" draggable={false} />
      </span>
    ) : null;

  const compact = density !== 'full';
  /** 竖屏环布下对手注额外置到 `.felt__chips`，pod 内一律不渲染（含 inline / trailing）。 */
  const suppressOppBet = betDecoupled && !player.isHuman;
  /**
   * `full` 档位下「本街投入」内联在筹码行：席位卡在环形布局里是**固定标称盒**
   * （算法按标称盒算容量与间距），注额若另起一行会让卡片长高 18px，
   * 上排就再也塞不进「公共牌带以上」的纵向预算里（实测 xl 只剩 136px）。
   */
  const inlineBet = !player.isHuman && density === 'full' && !suppressOppBet;
  const showBet = player.streetContributed > 0;
  const betInner = (
    <>
      <span className="chipstack" aria-hidden="true" />
      <span className="num">{chips(player.streetContributed)}</span>
    </>
  );
  const betNode = showBet && !suppressOppBet ? <div className="seat__bet">{betInner}</div> : null;
  /**
   * Round22 · F1（AC1.1/AC1.2）：英雄下注展示收敛为「唯一一条动作+金额 pill」。
   * 英雄行动态（有 `bubble`）时复用对手同源的 `pillInfo(kind, player)` 合并「动作+金额」，
   * 渲染为与 `.felt__actpill` 同规格（12/700、去筹码图标、`--pill-*` 语义色）的单条 pill，
   * 承载于英雄席底沿气泡位。文案格式与对手 100% 一致（如「跟注 354」/「加注 354」/「全下 X」）。
   * 与之配套：英雄侧不再渲染重复的 `.seat__bet`（🪙+数字）金额层（见 trailingBet），彻底消除叠字。
   */
  const heroPill = player.isHuman && bubble ? pillInfo(bubble.kind, player) : null;
  /**
   * QA-BUG-R5-DRIFT 演进（Round22 · F1）：英雄的「本街投入」金额层随 F1 一并移除
   * —— 金额已并入唯一的下注 pill（heroPill），不再另起尾随行。因为英雄席**恒不渲染**该行
   * （而非「有/无投入切换」），卡片盒高在同手内守恒，R5 防漂移目标仍成立（无需幽灵占位）。
   * 对手仍走原有 inline / trailing 注额路径，不受影响。
   */
  const trailingBet = inlineBet
    ? null
    : player.isHuman
      ? null
      : betNode;

  const cls = [
    'seat',
    player.isHuman ? 'seat--me' : '',
    isToAct ? 'seat--act' : '',
    player.folded ? 'seat--folded' : '',
    player.allIn ? 'seat--allin' : '',
    isWinner ? 'seat--winner' : '',
    player.eliminated ? 'seat--out' : '',
    compact ? 'seat--compact' : '',
    `seat--${density}`,
  ]
    .filter(Boolean)
    .join(' ');

  const statusText = player.eliminated
    ? '已淘汰'
    : player.folded
      ? '已弃牌'
      : isWinner
        ? '赢家'
        : player.allIn
          ? '全下'
          : thinking
            ? '思考中'
            : isToAct
              ? '行动中'
              : null;

  /**
   * MAJ-F3：窄档位的状态**文字编码**（设计规范 5.6 无障碍：全下 / 赢家 / 失误三类状态
   * 一律「颜色 + 形状 / 文字」双编码，DES-F3 也把状态徽标列为不可隐藏项）。
   *
   * 改前 `row` / `micro` 两档直接 `display:none` 掉整枚徽标，窄屏「全下」只剩一圈橙色描边、
   * 「已弃牌」只剩整卡降透明 —— 对色觉障碍用户等于状态信息丢失（终验收实测 sm / min360 均 0/3）。
   * 这两档的横向预算装不下「已弃牌」三个字（micro 标称盒只有 96×24），因此改为**单字**徽标：
   * 颜色照旧、文字压到 1 个字，宽度约 21px，卡片盒高度不变（徽标与筹码同处 row2 行内）。
   *
   * `micro` 再收一档，只保留三类**不可逆 / 决定性**状态（淘汰 / 弃牌 / 赢家 / 全下）：
   * 「思考中」「行动中」是短暂态，且已由高亮描边 + 行动计时条双编码，
   * 在 96px 宽里让位给名字与筹码，是信息量更大的选择。
   */
  const statusShort = player.eliminated
    ? '淘'
    : player.folded
      ? '弃'
      : isWinner
        ? '赢'
        : player.allIn
          ? '全'
          : thinking
            ? '思'
            : isToAct
              ? '行'
              : null;
  const criticalStatus = player.eliminated || player.folded || isWinner || player.allIn;
  const statusLabel =
    density === 'micro' ? (criticalStatus ? statusShort : null) : density === 'row' ? statusShort : statusText;

  /**
   * MAJ-F5：窄屏 HUD 的档位。
   *
   * 改前 HUD 只在 `full` 档渲染，`row` / `micro` 两档连 DOM 都没有，样式层再补一道
   * `display:none` —— 终验收实测 sm / min360 均 **0 项**，且没有任何 hover 替代路径，
   * 等于窄屏下「对手是什么打法」这条信息彻底消失（xl 是 3 项）。
   *
   * 设计规范 4.9「席位卡边界情况」写的是**降级**而不是删除：窄断点 HUD 降为 2 项，
   * 剩余指标进 hover 浮层。所以这里分三档：
   *   - `full`  → V / P / A 三项（与改前逐像素一致，宽屏零变化）；
   *   - `two`   → V / P 两项独占 `row2` 的第二行，AF 并进 hover 释义与 `aria-label`；
   *   - `off`   → 指标不进卡内，全部进 `aria-label` + 文字区 `title`。
   *
   * 为什么 `row` 档还要再看「本街是否有投入」：row 档标称盒 116×52，实测网格列宽只有
   * 98.7（min360 10p），盒内可用高度 44px = 13px 行高 × 3 行。三行的分配是
   * 「名字 / 筹码+状态 / 第三行」，第三行是**注额与指标的互斥槽**：
   *   - 本街有投入 → 第三行给注额。牌桌上「这家这条街投了多少」是当前决策的必需读数，
   *     指标是长期倾向，可以延后获取，所以让位的一定是指标；
   *   - 本街无投入 → 第三行给 V / P 两项指标（实测 46.7px ≤ 56.7px 文字区宽，装得下）。
   * 硬约束是「卡片自然高度永不超过标称盒 52px」——盒高是席位布局算法的输入量，
   * 撑高就会重演第 5 轮的席位互压（本轮实测正是它把状态徽标顶出卡片，见 MAJ-F3）。
   *
   * `micro` 档（96×24，盒内单行 20px）横向预算实测只剩 54px，名字 + 筹码 + 状态徽标
   * 已经排满，HUD 46.7px 放进去必被裁切（改前就是「有 DOM、看不清」）。这一档整体走
   * hover + 无障碍名，卡内不占位。
   */
  const hudMode: 'full' | 'two' | 'off' =
    density === 'full' ? 'full' : density === 'row' && betNode === null ? 'two' : 'off';
  const afText = stats.af === null ? '—' : stats.afCapped ? '99+' : stats.af.toFixed(1);
  /** 降级档把 AF 并进 hover 释义 / 无障碍名，保证指标本身不丢。 */
  const hudTipSuffix = hudMode === 'two' ? `；AF 激进度 ${afText}（（下注 + 加注）÷ 跟注）` : '';
  const hudAria = hudMode === 'two' ? `，AF ${afText}` : '';
  /**
   * 卡内不显示 HUD 的档位（`compact` / 带注额的 `row` / `micro`）：三个指标全部进
   * 「席位卡无障碍名 + 文字区 hover 提示」，指标可获取性不因档位而丢失（MAJ-F5 的实质要求）。
   */
  const hudFallback =
    hudMode === 'off' && density !== 'compact'
      ? `风格指标 VPIP ${fmtPct(stats.vpip)}、PFR ${fmtPct(stats.pfr)}、AF ${afText}`
      : null;

  /**
   * HUD 抽成变量，是因为**挂载位置随档位不同**：
   *   - `full`：自成一行（`.seat__body` 的第三行），与改前完全一致；
   *   - `two`（无注额的 row 档）：挂进 `.seat__row2`，CSS 用 `flex:1 0 100%` 让它
   *     换到该行的**第二行**（即卡内第三行），不与筹码 / 状态徽标争同一行的横向预算。
   * 为什么不能跟筹码同行：row 档实测文字区只有 56.7px（min360 10p），
   * 「筹码 32 + 徽标 19 + HUD 46.7」超额一倍，改前实测 HUD 可见比低到 0.25、
   * 徽标被筹码溢出文字压到 0.969。
   * 为什么不能再往下多加一行：卡片盒高 52px 是环形 / 网格布局算法的输入量，
   * 撑高就会引发席位互压（第 5 轮的老账），所以第三行由「注额 or 指标」互斥占用。
   */
  const hudNode =
    hudMode === 'off' ? null : (
      // HUD 是纯展示区：三个指标都不进入 Tab 序（设计方案无障碍要求 / 验收 MAJ-9），
      // 键盘 Tab 直达行动区；释义仍可 hover 查看，读屏可在浏览模式读到。
      //
      // MAJ-F5：窄档位（row）不再整块 `display:none`，而是按设计规范 4.9
      // 「席位卡边界情况：窄断点 HUD 降为 2 项，剩余指标进 hover 浮层」降级到 2 项。
      // 降级时 AF 不是被丢掉，而是并进 V / P 的 hover 释义（见 `hudTipSuffix`）；
      // 连 2 项都放不下的档位（micro / 有注额的 row）走 `hudFallback`，
      // 保证「指标仍可获取」这条不因为断点而丢失。
      <div className="seat__hud" role="group" aria-label={`${player.name} 风格指标${hudAria}`}>
        <Tip focusable={false} text={`VPIP：主动投入筹码入池的手数占比（样本 ≥20 手才显示）${hudTipSuffix}`}>
          <span className="hud__item">
            V <b className="num">{fmtPct(stats.vpip)}</b>
          </span>
        </Tip>
        <Tip focusable={false} text={`PFR：翻牌前加注的手数占比${hudTipSuffix}`}>
          <span className="hud__item">
            P <b className="num">{fmtPct(stats.pfr)}</b>
          </span>
        </Tip>
        {hudMode === 'full' ? (
          <Tip focusable={false} text="AF 激进度：（下注 + 加注）次数 ÷ 跟注次数">
            <span className="hud__item">
              A <b className="num">{afText}</b>
            </span>
          </Tip>
        ) : null}
      </div>
    );

  /**
   * R6 无障碍：默认态席位卡 aria-label 只带「昵称 / 状态 / 筹码（+指标兜底）」，风格文字进展开区，
   * 不剧透。唯一例外是 `micro` 档——头像视觉隐藏了，风格便并进 aria（沿用 hudFallback「指标不因档位丢」的口径）。
   */
  const styleAria = style !== null && density === 'micro' ? `，风格 ${styleName(style)}` : '';

  /**
   * R11-2 / R12-2：SB / BB / D 三角色徽标。位置为 absolute 贴卡片外沿角（`.seat__badges`），
   * **不占卡内横向、不参与标称盒高度**（与头像同策略，防漂移）。三重编码 = 颜色 + 文字 + aria：
   * 徽标本体 `aria-hidden`，角色文字统一并入席位卡 `aria-label`（文字主编码，不依赖颜色）。
   * heads-up 时庄家兼小盲（isButton && isSmallBlind）→ 同座并排显示 `庄 · 小盲`。
   *
   * R12-2：徽标文案由单/双字母（D / SB / BB）改为中文（庄 / 小盲 / 大盲）。文字是主编码，
   * 直白无需记忆缩写；尺寸方案见 layout.css（pill 随内容伸缩、min-width 兜底，2 字「小盲/大盲」
   * 完整显示不截断；absolute 贴外沿角、不撑高标称盒 → 不回退 R5 防漂移）。
   */
  const badges: { key: string; kind: string; text: string }[] = [];
  if (isButton) badges.push({ key: 'd', kind: 'd', text: '庄' });
  if (isSmallBlind) badges.push({ key: 'sb', kind: 'sb', text: '小盲' });
  if (isBigBlind) badges.push({ key: 'bb', kind: 'bb', text: '大盲' });
  const roleAria = [
    isButton ? '庄家位' : '',
    isSmallBlind ? '小盲位' : '',
    isBigBlind ? '大盲位' : '',
  ]
    .filter(Boolean)
    .join('兼');


  /**
   * 风格卡节点（R2-问题2）：抽为变量，便于「有 overlay 根则 portal、否则原地内联」两条路径复用。
   * className 带 `--portal`（position:fixed + z-eqfloat 覆盖）与 `--{placement}` 方向标记；
   * cardPos 就绪前 visibility:hidden 渲染一帧供 useLayoutEffect 测量自然高再定位（避免闪跳）。
   */
  const styleCard =
    canExpand && expanded && style !== null ? (
      <div
        ref={cardRef}
        className={`seat__stylecard seat__stylecard--portal${cardPos ? ` seat__stylecard--${cardPos.placement}` : ''}`}
        id={cardId}
        role="group"
        aria-label={`${player.name} 的打法风格`}
        style={
          cardPos
            ? { left: `${cardPos.left}px`, top: `${cardPos.top}px`, maxHeight: `${cardPos.maxHeight}px`, visibility: 'visible' }
            : { left: '0px', top: '0px', visibility: 'hidden' }
        }
      >
        <div className="stylecard__head">
          {avatarNode}
          <span className="stylecard__nick">{player.name}</span>
          <button type="button" className="stylecard__close" aria-label="收起风格卡" onClick={() => setExpanded(false)}>
            ✕
          </button>
        </div>
        <div className="stylecard__style">风格：{styleName(style)}</div>
        <p className="stylecard__desc">{STYLE_DESCRIPTIONS[style]}</p>
        <div className="stylecard__hud">
          VPIP {fmtPct(stats.vpip)} · PFR {fmtPct(stats.pfr)} · AF {afText}
        </div>
      </div>
    ) : null;


  return (
    <div
      ref={rootRef}
      className={cls}
      data-inner-side={innerSide}
      aria-label={`${player.name}${statusText ? `，${statusText}` : ''}${roleAria ? `，${roleAria}` : ''}，筹码 ${player.stack}${hudFallback ? `，${hudFallback}` : ''}${styleAria}`}
    >
      {bubble ? (
        player.isHuman ? (
          // Round22 · F1：英雄行动态渲染唯一一条「动作+金额」pill（复用对手 pillInfo，去筹码图标）。
          heroPill ? (
            <div className={`bubble bubble--pill pill--${heroPill.cls}`} aria-hidden="true">
              {heroPill.text}
            </div>
          ) : null
        ) : (
          <div className={`bubble bubble--${bubble.kind}`}>{bubble.text}</div>
        )
      ) : null}

      {badges.length && !satellitesDecoupled ? (
        <div className="seat__badges" aria-hidden="true">
          {badges.map((b) => (
            <span key={b.key} className={`seat__badge seat__badge--${b.kind}`}>
              {b.text}
            </span>
          ))}
        </div>
      ) : null}


      <div className="seat__cards" data-inner-side={innerSide}>
        {/* Round23 FR-4：全员亮牌复盘（reveal）时，即便已弃牌 / 已不在手（inHand=false）也亮底牌，
            通过 `dimmed`（灰阶）+ 席位卡 `seat--folded`（整组降透明）+ 「已弃牌」状态区分。 */}
        {!satellitesDecoupled && player.hole && (player.inHand || reveal) ? (
          reveal ? (
            <>
              <PlayingCard card={player.hole[0]} size={player.isHuman ? 'hero' : 'sm'} dimmed={player.folded} highlight={winningCards.has(player.hole[0])} dealIndex={0} />
              <PlayingCard card={player.hole[1]} size={player.isHuman ? 'hero' : 'sm'} dimmed={player.folded} highlight={winningCards.has(player.hole[1])} dealIndex={1} />
            </>
          ) : (
            <>
              <PlayingCard faceDown size="sm" />
              <PlayingCard faceDown size="sm" />
            </>
          )
        ) : null}

        {/* FR-5（偏差①）：英雄牌型 badge 迁到英雄手牌**左缘外**（挂在 .seat__cards 内绝对定位，
            right:100% + 6px、垂直居中对齐手牌），不再落在手牌正下方居中；--z-seat:1 不越公共牌带 40。
            窄屏空间不足时由 CSS 兜底（缩小 + 上移到手牌左上角），不回到正下方居中，不出屏。 */}
        {player.isHuman && categoryLabel ? (
          <div className="hero__cat" aria-label={`当前牌型：${categoryLabel}`}>
            {categoryLabel}
          </div>
        ) : null}
      </div>

      {/* HUD 不进卡内的档位，指标走文字区的 hover 提示（`aria-label` 已带同一份数值） */}
      <div className="seat__body" title={hudFallback ?? undefined}>
        <div className="seat__row1">
          {/*
           * R6：对手的「头像 + 昵称」是展开风格卡的触发器（button，aria-expanded），
           * 键盘可聚焦、有 aria-label；风格中文名从这里移除（改默认隐藏，进展开区）。
           * 人类座位无风格，仍是纯文本昵称。
           */}
          {canExpand ? (
            <button
              ref={triggerRef}
              type="button"
              className="seat__trigger"
              aria-expanded={expanded}
              aria-controls={expanded ? cardId : undefined}
              aria-label={`查看 ${player.name} 的打法风格`}
              title={player.name}
              onClick={() => setExpanded((v) => !v)}
            >
              {avatarNode}
              <span className="seat__name">{nameChildren}</span>
            </button>
          ) : (
            <span className="seat__name" title={player.name}>
              {nameChildren}
            </span>
          )}
        </div>
        <div className="seat__row2">
          {/* row 档横向不够时筹码是唯一允许截断的字段（状态徽标不可挤），`title` 兜住精确值 */}
          <span className="seat__stack num" title={chips(player.stack)}>
            {chips(player.stack)}
          </span>
          {statusLabel ? (
            <span
              className={`seat__status seat__status--${statusKind(player, thinking, isToAct, isWinner)}`}
              title={statusText ?? undefined}
            >
              {statusLabel}
            </span>
          ) : null}
          {inlineBet ? betNode : null}
          {hudMode === 'two' ? hudNode : null}
        </div>
        {hudMode === 'full' ? hudNode : null}
        {showdownValue ? <div className="seat__made">{showdownValue}</div> : null}
      </div>

      {/*
       * R6 风格卡（展开区）：风格名 + 一句性格描述 +（样本≥20 手才显示的）VPIP/PFR/AF。
       * R2-问题2：改为 portal 渲染到 overlay 根 `.gs__overlay`（有效层级 --z-eqfloat:110），
       * 脱离毛毡裁剪上下文、越过公共牌带 40；位置由 placeStyleCard 按触发器屏幕位置翻转 + 四边 clamp
       * 求解（cardPos）。cardPos 未就绪时以 visibility:hidden 渲染一帧供测量自然高，避免闪跳。
       * overlay 根缺失时（如隔离渲染的单测环境）退化为原地内联渲染，功能不变。
       */}
      {styleCard && overlayRoot ? createPortal(styleCard, overlayRoot) : styleCard}

      {trailingBet}

      {clockRatio !== null ? (
        <div className="seat__clock" role="timer" aria-label="行动时限">
          <span className="seat__clock-bar" style={{ width: `${Math.max(0, Math.min(1, clockRatio)) * 100}%` }} />
        </div>
      ) : null}
    </div>
  );
}

function statusKind(p: Player, thinking: boolean, isToAct: boolean, isWinner: boolean): string {
  if (p.eliminated) return 'out';
  if (p.folded) return 'folded';
  if (isWinner) return 'winner';
  if (p.allIn) return 'allin';
  if (thinking) return 'think';
  if (isToAct) return 'act';
  return 'idle';
}

function fmtPct(v: number | null): string {
  return v === null ? '—' : `${Math.round(v)}%`;
}
